import type { ReactNode } from 'react'
import { usePage } from '../api'
import { useLang } from '../lang'
import { Markdown } from './Markdown'
import { StateLine } from '../design'

// A standing prose page from the station profile — the privacy notice, the letter description,
// the tech and hardware behind the station.
// One readable column rather than About's two-column grid: these are documents somebody reads
// through in order, often having followed a link from an email footer, not a page they browse.
//
// The first `## ` heading is promoted out of the flow to be the page title; everything after it
// renders as ordinary markdown. A station shipping no file for this page renders nothing at all
// (the caller hides the link), because a blank privacy page is worse than no link to one.
export function StationPageTab({
  slug,
  children,
}: {
  slug: 'privacy' | 'letter' | 'tech'
  children?: ReactNode
}) {
  const { data, isLoading, isError } = usePage(slug)
  const { lang } = useLang()

  if (isLoading || !data) {
    return <StateLine busy={!isError}>{isError ? '—' : '…'}</StateLine>
  }

  // Irish only when the station has actually written it. An unproofed machine translation of a
  // privacy notice would be worse than English — same discipline as the lore's ga_verified.
  const md = ((lang === 'ga' && data.ga?.trim() ? data.ga : data.en) ?? '').trim()
  if (!md) return null

  const lines = md.replace(/\r\n/g, '\n').split('\n')
  const title = lines[0].startsWith('## ') ? lines[0].slice(3).trim() : null
  const body = title ? lines.slice(1).join('\n') : md

  return (
    <section className="ed-page">
      {title && <h1 className="ed-page-title">{title}</h1>}
      <div className="ed-page-body">
        <Markdown source={body} />
      </div>
      {children}
    </section>
  )
}
